// import router from "@/router";
import { getApiLang, i18nt } from '@/i18n';
import { showLoading, toPage } from '@/utils';
import { resolveApiErrorToastMessage } from '@/utils/request/apiErrorMessage';
import { storage } from '@/plugins/ionic-storage';
import { useTokenStore, useUserStore } from '@/store';
import axios, { AxiosResponse } from 'axios';
import { showToast, ToastWrapperInstance, showConfirmDialog } from 'vant';
// import { ImLogout } from '@/apis/chatApi';
import { refreshTokenAction as refresh } from './refreshToken';
import { disconnectWs } from '@/views/conversation/socket';
import { useChatHistoryStore } from '@/store/chatHistory';
declare module 'axios' {
  interface AxiosResponse {
    [key: string]: any;
    // 这里追加你的参数
  }
  export function create(config?: AxiosRequestConfig): AxiosInstance;
}

type RetryRequest = {
  config: any;
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
};



const baseURL = import.meta.env.VITE_BASE_URL;
// const router = useIonRouter();
const service = axios.create({
  //设置超时时间，单位毫秒//设置超时时间，单位毫秒
  baseURL,
  timeout: 300 * 1000, // 5分钟
});

let isLoading = 0;
let loadingInstance: ToastWrapperInstance | undefined;

const requestQueue: RetryRequest[] = []; // 缓存的请求队列

/**
 * Token 刷新成功后，按照进入队列的顺序重新发起请求。
 */
async function releaseRequestQueue() {
  const queue = requestQueue.splice(0, requestQueue.length);

  for (const item of queue) {
    try {
      const accessToken = useTokenStore().token.accessToken;

      item.config.headers = item.config.headers || {};
      item.config.headers.Authorization = accessToken;

      // 必须使用 service，才能保留 baseURL 和项目拦截器。
      const result = await service(item.config);
      item.resolve(result);
    } catch (error) {
      item.reject(error);
    }
  }
}
/**
 * Token 刷新失败，拒绝所有暂存请求。
 */
function rejectRequestQueue(error: unknown) {
  const queue = requestQueue.splice(0, requestQueue.length);

  queue.forEach((item) => {
    item.reject(error);
  });
}

// 保存请求的数据
service.interceptors.request.use(
  async (config) => {
    if (isLoading === 0 && !config.hideLoading) {
      loadingInstance = showLoading({ delay: 3000 });
    }
    isLoading++;
    let { token: { accessToken } } = useTokenStore();
    accessToken = accessToken || (await storage.get('accessToken'));

    if (accessToken) {
      config.headers.Authorization = `${accessToken}`;
    }
    (config.headers as any).lang = getApiLang();
    return config;
  },
  (error) => {
    Promise.reject(error);
  },
);
/**
 * 响应拦截器
 */
const errHandle = (error: AxiosResponse) => {
  if (isLoading == 1) {
    loadingInstance && loadingInstance.close();
    loadingInstance = undefined;
  }
  isLoading--;
  if (error.status >= 500) {
    showToast(i18nt('wlcwqshzs'));
  }
  return Promise.reject(error);
};

const successHandle = async (response: AxiosResponse) => {
  const { config } = response;
  if (isLoading == 1) {
    loadingInstance && loadingInstance.close();
    loadingInstance = undefined;
  }
  isLoading--;
  const data = 'data' in response.data ? response.data.data : response.data;
  if (data instanceof Blob) {
    return Promise.resolve(URL.createObjectURL(data));
  }
  const { code, msg, msgKey } = response.data;

  if (code === 1003000003) {
    showConfirmDialog({
      confirmButtonText: i18nt('qcz_1'),
      cancelButtonText: i18nt('xczs'),
      title: i18nt('yebz'),
      message: i18nt('ndyebzqcz'),
    }).then(() => {
      toPage('/wallet');
    });

    return Promise.reject(response);
  }

  const logout = async () => {
    const { clearUser } = useUserStore();
    const chatHistoryStore = useChatHistoryStore();
    const query: {
      [key: string]: string;
    } = {};
    if (location.pathname == '/grass' && location.search) {
      const params = location.search.split('?')[1];
      params.split('&').forEach((item) => {
        const [key, value] = item.split('=');
        query[key] = value;
      });
    }
    if (location.pathname !== '/login') {
      console.log(location.search);
      showToast(resolveApiErrorToastMessage(msgKey, msg, 'dqdlygq'));
      toPage(`/login`, false, query);
      try {
        disconnectWs();
        chatHistoryStore.resetSession();
        await clearUser();
        // await storage.remove('accessToken');
        // ImLogout();
      } catch (err) {
        toPage(`/login`, false, query);
      }
    }
  };

  if (code == 0) {
    return Promise.resolve(data);
  }

  if (code === 401) {
    const retryConfig = config as any;

    // refresh-token 接口自己返回 401，说明 refresh token 也已失效。
    // 不能再次进入队列，否则会产生死锁。
    if (retryConfig.skipAuthRefresh) {
      rejectRequestQueue(response);
      await logout();
      return Promise.reject(response);
    }

    // 使用新 Token 重试后仍返回 401，禁止无限刷新。
    if (retryConfig._authRetry) {
      rejectRequestQueue(response);
      await logout();
      return Promise.reject(response);
    }

    retryConfig._authRetry = true;

    const tokenStore = useTokenStore();

    // 当前请求先进入队列。Promise 会在请求重放完成后 resolve/reject。
    const pendingRequest = new Promise((resolve, reject) => {
      requestQueue.push({
        config: retryConfig,
        resolve,
        reject,
      });
    });

    // 已经有请求在刷新 Token，当前请求只需留在队列中等待。
    if (tokenStore.isTokenRefreshing) {
      try {
        await tokenStore.refreshTokenFn();
        await releaseRequestQueue();
      } catch (refreshError) {
        rejectRequestQueue(refreshError);
        await logout();
      }
      return pendingRequest;
    }

    try {
      // 第一个收到 401 的请求负责发起刷新。
      await tokenStore.refreshTokenFn();

      // TokenStore 更新成功后，依次重新发起队列中的请求。
      await releaseRequestQueue();
    } catch (refreshError) {
      // 刷新失败，所有暂存请求都必须 reject，不能永久 pending。
      rejectRequestQueue(refreshError);
      await logout();
    }

    return pendingRequest;

    // return Promise.reject(response);
  }
  if (code) {
    if (!config.customTip) {
      showToast(resolveApiErrorToastMessage(msgKey, msg));
    }
    return Promise.reject(response);
  }
  return Promise.resolve(response.data);
};

service.interceptors.response.use(successHandle, errHandle);

export const postRequest = (url: string, data = {}, opts = {}) => {
  return service({
    url,
    method: 'post',
    data,
    ...opts,
  });
};

// 获取二进制流
export const getBlobRequest = (url: string, data = {}) => {
  return service({
    url,
    method: 'get',
    responseType: 'blob',
    params: data,
  });
};

export const getRequest = (url: string, data = {}, opts = {}) => {
  return service({
    url,
    method: 'get',
    params: data,
    ...opts,
  });
};

export const putRequest = (url: string, data = {}, opts = {}) => {
  return service({
    url,
    method: 'put',
    data,
    ...opts,
  });
};

export const delRequest = (url: string, data = {}) => {
  return service({
    url,
    method: 'delete',
    data,
  });
};
// export default service;

// 充值后跳转有问题
// 通告卡购买记录

export default {
  postRequest,
  getRequest,
  putRequest,
  delRequest,
  getBlobRequest,
};
